import classNames from 'classnames/bind';
import parse, {
  HTMLReactParserOptions,
  attributesToProps,
  domToReact,
} from 'html-react-parser';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams } from '../../hooks/useParams';
import styles from './index.module.scss';

interface OEmbed {
  code?: string;
  html?: string;
  width?: number;
  height?: number;
  type?: string;
  providerName?: string;
  provider_name?: string;
  aspectRatio?: number;
}

export interface EmbedProps {
  id?: any;
  oembed?: OEmbed;
  url?: string;
  text?: string;
  editor?: boolean;
  [key: string]: any;
}

export const Embed = ({
  id,
  oembed,
  url,
  text,
  editor,
  ...rest
}: EmbedProps) => {
  const [embed, setEmbed] = useState<any>(oembed);
  const embedContainer = useRef<HTMLDivElement>(null);
  const cx = classNames.bind(styles);
  const { paramsString } = useParams();

  const options: HTMLReactParserOptions = {
    replace: (domNode: any) => {
      if (
        domNode.name === 'a' &&
        domNode.attribs &&
        !domNode.attribs.href.startsWith('mailto:')
      ) {
        const props = attributesToProps(domNode.attribs);
        return (
          <a
            {...props}
            href={`/${id}/outbound?url=${encodeURIComponent(
              domNode.attribs.href,
            )}&${paramsString}`}
            rel="noopener noreferrer"
            target="_blank"
          >
            {domToReact(domNode.children, options)}
          </a>
        );
      }
    },
  };

  const getEmbed = async (uri?: string) => {
    try {
      if (!uri) return null;
      const res = await fetch(
        `https://oembed-khaki.vercel.app/api/embed?url=${encodeURIComponent(
          uri,
        )}`,
      );
      const json = await res.json();
      setEmbed(json);
    } catch (e) {
      setEmbed(undefined);
      console.log(e);
      return null;
    }
  };

  useEffect(() => {
    if (url || !oembed?.hasOwnProperty('code')) {
      getEmbed(url);
    }
  }, [url, oembed]);

  const handleInteraction = (e: any) => {
    const iframe = embedContainer.current?.querySelector('iframe');
    const active = document.activeElement;

    if (active?.isSameNode(iframe as Node) || active == iframe) {
      if (!iframe?.dataset.sent && iframe) {
        iframe.dataset.sent = 'true';

        const data = {
          detail: {
            type: 'interaction',
            data: {
              src: iframe?.src,
            },
          },
        };

        const event = new CustomEvent('track', data);
        window.dispatchEvent(event);
      }
    }
  };

  useLayoutEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('blur', handleInteraction);
      return () => window.removeEventListener('blur', handleInteraction);
    }
  }, []);

  if (
    !embed &&
    !embed?.hasOwnProperty('code') &&
    !embed?.hasOwnProperty('html')
  ) {
    if (editor) {
      return (
        <div className={cx('component', 'embed', rest)}>
          Embed not supported
        </div>
      );
    }
    return <></>;
  }

  const aspectRatio = embed.aspectRatio
    ? embed.aspectRatio
    : embed.width && embed.height
      ? (embed.height / embed.width) * 100
      : (9 / 16) * 100;

  return (
    <section
      className={cx(
        'component',
        'embed',
        embed.type,
        `provider${embed.providerName ?? embed.provider_name}`,
        rest,
      )}
    >
      <figure
        className={styles.embedFigure}
        style={{ aspectRatio: `${embed.width ?? 16}/${embed.height ?? 9}` }}
      >
        <div
          className={styles.embedFigureSpacer}
          style={{ paddingTop: `${aspectRatio}%` }}
        />
        <div
          ref={embedContainer}
          dangerouslySetInnerHTML={{ __html: embed.code || embed.html || '' }}
        />
      </figure>

      {!!text && text !== '<p><br/></p>' && (
        <figcaption className={styles.caption}>
          {parse(text, options)}
        </figcaption>
      )}
    </section>
  );
};

Embed.defaultProps = {
  oembed: {},
} as Partial<EmbedProps>;
